import { createContext, useContext, useState, useMemo, useRef, useEffect } from 'react'
import { removeUnitOfMeasurement } from '../../utils/css'
import { useConfig } from '../config'

const HeaderHeightContext = createContext()
const HeaderHeightFilledContext = createContext()
const HeaderHeightFilledApiContext = createContext()
const HeaderStickyTopContext = createContext()
const HeaderStickyTopApiContext = createContext()

const HeaderProvider = ({ children }) => {
	const {
		stickyHeader: { enabled: stickyHeaderEnabled, hideOnScrollDown: stickyHeaderHideOnScrollDown },
	} = useConfig()

	const [height, setHeight] = useState(0)
	const [heightFilled, setHeightFilled] = useState(0)
	const [stickyTop, setStickyTop] = useState(0)
	const ref = useRef()

	useEffect(() => {
		/**
		 * Can be used to enable css property.
		 * eg: transition: var(--if-not-sticky-hide-on-scroll-down, var(--sticky-transition-value));
		 * eg: transition: var(--if-not-sticky-hide-on-scroll-down, top .2s ease-out );
		 * eg: color: var(--if-not-sticky-hide-on-scroll-down, red );
		 */
		if (!stickyHeaderEnabled || !stickyHeaderHideOnScrollDown) {
			document.documentElement.style.setProperty(
				'--if-not-sticky-hide-on-scroll-down',
				'invalid-value-so-default-value-is-used'
			)
		}
	}, [stickyHeaderEnabled, stickyHeaderHideOnScrollDown])

	useEffect(() => {
		document.documentElement.style.setProperty('--header-sticky-top', `${stickyTop}px`)
	}, [stickyTop])

	useEffect(() => {
		document.documentElement.style.setProperty('--header-height', `${height}px`)
	}, [height])

	const valueHeight = useMemo(() => [{ height, ref }, { setHeight }], [height])
	const valueHeightFilled = useMemo(() => ({ height: heightFilled }), [heightFilled])
	const valueHeightFilledApi = useMemo(() => ({ setHeight: setHeightFilled }), [])
	const valueStickyTop = useMemo(() => ({ stickyTop, ref }), [stickyTop])
	const valueStickyApi = useMemo(() => ({ setStickyTop }), [])

	return (
		<HeaderHeightContext.Provider value={valueHeight}>
			<HeaderHeightFilledContext.Provider value={valueHeightFilled}>
				<HeaderHeightFilledApiContext.Provider value={valueHeightFilledApi}>
					<HeaderStickyTopContext.Provider value={valueStickyTop}>
						<HeaderStickyTopApiContext.Provider value={valueStickyApi}>{children}</HeaderStickyTopApiContext.Provider>
					</HeaderStickyTopContext.Provider>
				</HeaderHeightFilledApiContext.Provider>
			</HeaderHeightFilledContext.Provider>
		</HeaderHeightContext.Provider>
	)
}

export default HeaderProvider

export const useHeaderHeight = () => useContext(HeaderHeightContext)
export const useHeaderHeightFilledValue = () => useContext(HeaderHeightFilledContext)
export const useHeaderHeightFilledApi = () => useContext(HeaderHeightFilledApiContext)
export const useHeaderStickyTopValue = () => useContext(HeaderStickyTopContext)
export const useHeaderStickyTopApi = () => useContext(HeaderStickyTopApiContext)
export const useHeaderStickyTop = () => {
	const stickyTopValue = useHeaderStickyTopValue()
	const stickyTopApi = useHeaderStickyTopApi()

	return useMemo(() => [stickyTopValue, stickyTopApi], [stickyTopValue, stickyTopApi])
}

/**
 * @deprecated
 */
export const useHeader = () => {
	const [heightValue, heightApi] = useHeaderHeight()
	const [stickyTopValue, stickyTopApi] = useHeaderStickyTop()

	return useMemo(
		() => [
			{ ...heightValue, ...stickyTopValue },
			{ ...heightApi, ...stickyTopApi },
		],
		[heightApi, heightValue, stickyTopApi, stickyTopValue]
	)
}

export const useHeaderHeightFilled = (headerType, headerHeight) => {
	const { setHeight } = useHeaderHeightFilledApi()
	useEffect(() => {
		const headerFilledHeightValue = headerType === 'transparent' ? 0 : headerHeight
		document.documentElement.style.setProperty('--header-height-filled', `${headerFilledHeightValue}px`)
		setHeight(headerFilledHeightValue)
	}, [headerHeight, headerType, setHeight])
}

export const getHeaderHeight = () =>
	removeUnitOfMeasurement(document.documentElement.style.getPropertyValue('--header-height'))
export const getHeaderStickyTop = () =>
	removeUnitOfMeasurement(document.documentElement.style.getPropertyValue('--header-sticky-top'))
export const getHeaderHeightFilled = () =>
	removeUnitOfMeasurement(document.documentElement.style.getPropertyValue('--header-height-filled'))
